import React, { useState } from 'react';
import UnstyledButton from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/UnstyledFilterButton';
import _clone from 'lodash/clone';
import {
  ButtonGroup,
  EditInput
} from 'src/components/BeaconV2Components/GenericSidebarNav/AdvertisingEditFields/EditInput';
import { SelectedIncludeCheckbox, SelectExcludeCheckbox, SelectIncludeCheckbox } from 'src/components/SvgIcons';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { z } from 'zod';
import { GenericModal } from 'src/components/Modals/GenericModal';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';

import { fetchMainEntity } from 'src/store/modules/entityService/operations';
import { useDispatch } from 'react-redux';
import { updateCampaign } from 'src/components/AdManager/AmsUtils';
import { CustomLoading } from 'src/components/common/Loading/PlaceHolderLoading/PlaceHolderLoading';
import { checkInstacartRetailer } from 'src/utils/app';

export const MINIMUM_BUDGET_MIN_VALUE = 1; // $1 as minimum value
export const MINIMUM_BUDGET_MAX_VALUE = 10000; // $10,000 as maximum value
export const DAILY_BUDGET_THRESHOLD_ERROR =
  'Daily budget cannot be less than the minimum budget. Please increase your budget.';

const createMinimumBudgetSchema = (currentDailyBudget: number) =>
  z.object({
    minimumBudget: z
      .number()
      .min(MINIMUM_BUDGET_MIN_VALUE, `Minimum budget must be higher than ${MINIMUM_BUDGET_MIN_VALUE}`)
      .max(MINIMUM_BUDGET_MAX_VALUE, `Minimum budget must be less than ${MINIMUM_BUDGET_MAX_VALUE}`)
      .max(currentDailyBudget, DAILY_BUDGET_THRESHOLD_ERROR)
  });

const MinimumCampaignBudget = () => {
  const { showSnackbar } = useSnackbar();
  const [isFocus, setIsFocus] = useState(false);
  const currentEntity = useAppSelector((state) => state.entityService.mainEntity);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const currentMinimumBudget =
    currentEntity?.extendedAttributes?.automationAttributes?.metricsThresholds?.budgetAmountMinimum;
  const currentDailyBudget = currentEntity?.extendedAttributes?.liveBudgetSetting?.amount || 0;

  const [minimumBudget, setMinimumBudget] = useState<string | undefined>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingBudgetValue, setPendingBudgetValue] = useState<number>(0);

  // Reset the input value. Display placeholder value.
  const resetMinimumBudget = () => {
    setMinimumBudget(undefined);
    setIsFocus(false);
  };

  const validateMinimumBudget = () => {
    const minimumBudgetValue = +minimumBudget || MINIMUM_BUDGET_MIN_VALUE;

    const schema = createMinimumBudgetSchema(currentDailyBudget);
    const validationResult = schema.safeParse({ minimumBudget: minimumBudgetValue });

    if (!validationResult.success) {
      const errorMessage = validationResult.error.errors[0]?.message || 'Invalid minimum budget value';
      showSnackbar({
        message: errorMessage,
        type: 'error'
      });
      resetMinimumBudget();
      return;
    }

    // Validation passed, show confirmation modal
    setPendingBudgetValue(minimumBudgetValue);
    setShowConfirmModal(true);
  };

  const saveMinimumBudget = async () => {
    try {
      const mainEntityCloned: any = _clone(currentEntity);
      delete mainEntityCloned.adCampaignProducts;
      delete mainEntityCloned.adTargets;
      delete mainEntityCloned.adGroups;

      // Ensure metricsThresholds exists before setting budgetAmountMinimum
      if (!mainEntityCloned.extendedAttributes.automationAttributes.metricsThresholds) {
        mainEntityCloned.extendedAttributes.automationAttributes.metricsThresholds = {
          budgetAmountMinimum: MINIMUM_BUDGET_MIN_VALUE
        };
      }
      mainEntityCloned.extendedAttributes.automationAttributes.metricsThresholds.budgetAmountMinimum =
        pendingBudgetValue;
      setLoading(true);
      await updateCampaign(mainEntityCloned);
      dispatch(
        fetchMainEntity(mainEntityCloned.type, mainEntityCloned.id, mainEntityCloned.extendedAttributes.retailerId)
      );
      showSnackbar({
        message: 'Your minimum budget was successfully updated.',
        type: 'success'
      });
      resetMinimumBudget();
      setLoading(false);
    } catch (error) {
      showSnackbar({
        message: 'Error saving minimum budget, please try again later.',
        type: 'error'
      });
      console.error('Error saving minimum budget', error);
      resetMinimumBudget();
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    saveMinimumBudget();
  };

  const handleCancelSave = () => {
    setShowConfirmModal(false);
    setPendingBudgetValue(0);
    resetMinimumBudget();
  };

  return (
    <div>
      <div
        className="field-title"
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '12px'
        }}
      >
        Minimum Budget
        <ButtonGroup className={isFocus ? 'focus' : ''}>
          <UnstyledButton onClick={() => {}}>
            {minimumBudget ? (
              <SelectedIncludeCheckbox onClick={validateMinimumBudget} style={{ width: '16px', height: '16px' }} />
            ) : (
              <SelectIncludeCheckbox style={{ width: '16px', height: '16px' }} />
            )}
          </UnstyledButton>
          <UnstyledButton
            onClick={() => {
              setIsFocus(false);
              setMinimumBudget(undefined);
            }}
          >
            <SelectExcludeCheckbox style={{ width: '16px', height: '16px' }} />
          </UnstyledButton>
        </ButtonGroup>
      </div>
      <div className="field-subtitle">
        {loading ? (
          <CustomLoading height={12} width={88} padding={0} />
        ) : (
          <EditInput
            onFocus={() => {
              setIsFocus(true);
            }}
            onBlur={() => {
              if (!minimumBudget || minimumBudget === currentMinimumBudget) {
                setIsFocus(false);
              }
            }}
            disabled={checkInstacartRetailer()}
            prefix="$"
            placeholder={currentMinimumBudget}
            value={minimumBudget}
            onChange={(e) => setMinimumBudget(e.target.value)}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <GenericModal
        open={showConfirmModal}
        onClose={handleCancelSave}
        sx={{
          '& .MuiPaper-root': {
            width: '500px',
            borderRadius: '8px',
            padding: '24px 24px 22px 24px',
            overflow: 'hidden',
            height: '178px'
          },
          '& .genericModal__header': {
            height: '100%'
          },
          '& .genericModal__header h4': {
            fontSize: '20px',
            fontWeight: 700,
            lineHeight: '28px'
          },
          '& .genericModal__body': {
            marginTop: '16px',
            height: '100%'
          }
        }}
      >
        <GenericModal.Header title="Set Minimum Budget" onClose={handleCancelSave} />
        <GenericModal.Body>
          <Text
            variant="body2"
            sx={{
              height: '40px',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px'
            }}
          >
            Are you sure you want to set the minimum budget for this campaign at ${pendingBudgetValue.toFixed(2)}?
            Minimum budgets can lead to overpacing.
          </Text>
          <Flex justifyContent="flex-end" sx={{ gap: '16px', marginTop: '20px' }}>
            <SlButton
              onClick={handleCancelSave}
              sx={{
                width: '92px',
                height: '32px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px'
              }}
            >
              Cancel
            </SlButton>
            <SlButton
              variant="contained"
              onClick={handleConfirmSave}
              sx={{
                width: '84px',
                height: '32px',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px'
              }}
            >
              Confirm
            </SlButton>
          </Flex>
        </GenericModal.Body>
      </GenericModal>
    </div>
  );
};

export default MinimumCampaignBudget;
